// [TAGS-V2-NO-CATEGORIA] Catálogo canônico compartilhado de tags do CRM
import { create } from 'zustand';
import { CrmTag } from '@/types';
import { apiCall } from '@/services/api';

interface TagsState {
  tagsByLoja: Record<string, CrmTag[]>;
  loadingByLoja: Record<string, boolean>;
  errorByLoja: Record<string, string | null>;

  fetchTagsForLoja: (lojaId: string, force?: boolean) => Promise<CrmTag[]>;
  createTag: (payload: {
    loja_id: string;
    nome_exibicao: string;
    cor?: string;
    descricao?: string | null;
    ia_pode_usar?: boolean;
  }) => Promise<CrmTag>;
  updateTag: (
    id: string,
    loja_id: string,
    updates: {
      nome_exibicao?: string;
      cor?: string;
      descricao?: string | null;
      ativa?: boolean;
      ia_pode_usar?: boolean;
    }
  ) => Promise<CrmTag>;
  archiveTag: (id: string, loja_id: string) => Promise<void>;
  reactivateTag: (id: string, loja_id: string) => Promise<CrmTag>;
  resolveTag: (
    lojaId: string | null | undefined,
    tagKey: string
  ) => { label: string; color: string; ativa: boolean; isKnown: boolean; id?: string };
}

export const useTagsStore = create<TagsState>((set, get) => ({
  tagsByLoja: {},
  loadingByLoja: {},
  errorByLoja: {},

  fetchTagsForLoja: async (lojaId: string, force = false) => {
    if (!lojaId || lojaId === 'all') return [];

    const currentTags = get().tagsByLoja[lojaId];
    const isLoading = get().loadingByLoja[lojaId];

    if (currentTags && currentTags.length > 0 && !force && !isLoading) {
      return currentTags; // cache hit: NÃO setar loading, NÃO re-renderizar
    }
    if (isLoading) {
      return get().tagsByLoja[lojaId] || []; // já carregando: não re-setar loading
    }

    set(state => ({
      loadingByLoja: { ...state.loadingByLoja, [lojaId]: true },
      errorByLoja: { ...state.errorByLoja, [lojaId]: null }
    }));

    try {
      // Busca todas as tags da loja (ativas e arquivadas para resolver badges e permitir reativação)
      const res = await apiCall(`/api/tags?loja_id=${encodeURIComponent(lojaId)}`);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const tagsList: CrmTag[] = Array.isArray(data)
        ? data
        : (Array.isArray(data?.tags) ? data.tags : []);

      set(state => ({
        tagsByLoja: { ...state.tagsByLoja, [lojaId]: tagsList },
        loadingByLoja: { ...state.loadingByLoja, [lojaId]: false }
      }));

      return tagsList;
    } catch (err: any) {
      console.error(`[TAGS-STORE] Erro ao buscar tags da loja ${lojaId}:`, err);
      set(state => ({
        loadingByLoja: { ...state.loadingByLoja, [lojaId]: false },
        errorByLoja: { ...state.errorByLoja, [lojaId]: err.message || 'Falha ao carregar tags' },
        tagsByLoja: { ...state.tagsByLoja, [lojaId]: state.tagsByLoja[lojaId] ?? [] }
      }));
      return get().tagsByLoja[lojaId] ?? [];
    }
  },

  createTag: async payload => {
    const { loja_id, nome_exibicao, cor, descricao, ia_pode_usar } = payload;
    const res = await apiCall('/api/tags', {
      method: 'POST',
      body: JSON.stringify({
        loja_id,
        nome_exibicao: nome_exibicao.trim(),
        cor: cor || '#3B82F6',
        descricao: descricao?.trim() || null,
        ia_pode_usar: Boolean(ia_pode_usar),
        ativa: true
      })
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      const err: any = new Error(errData.error || 'Erro ao criar tag');
      err.status = res.status;
      err.is_archived = errData.is_archived;
      err.conflict_id = errData.conflict_id;
      throw err;
    }

    const newTag: CrmTag = await res.json();
    set(state => {
      const existing = state.tagsByLoja[loja_id] || [];
      const updated = [...existing.filter(t => t.id !== newTag.id), newTag].sort((a, b) =>
        (a.nome_exibicao || a.nome).localeCompare(b.nome_exibicao || b.nome)
      );
      return {
        tagsByLoja: { ...state.tagsByLoja, [loja_id]: updated }
      };
    });

    return newTag;
  },

  updateTag: async (id, loja_id, updates) => {
    const res = await apiCall(`/api/tags/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      const err: any = new Error(errData.error || 'Erro ao atualizar tag');
      err.status = res.status;
      throw err;
    }

    const updatedTag: CrmTag = await res.json();
    set(state => {
      const existing = state.tagsByLoja[loja_id] || [];
      const updated = existing.map(t => (t.id === id ? { ...t, ...updatedTag } : t));
      return {
        tagsByLoja: { ...state.tagsByLoja, [loja_id]: updated }
      };
    });

    return updatedTag;
  },

  archiveTag: async (id, loja_id) => {
    const res = await apiCall(`/api/tags/${id}`, {
      method: 'DELETE'
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.error || 'Erro ao arquivar tag');
    }

    set(state => {
      const existing = state.tagsByLoja[loja_id] || [];
      const updated = existing.map(t => (t.id === id ? { ...t, ativa: false } : t));
      return {
        tagsByLoja: { ...state.tagsByLoja, [loja_id]: updated }
      };
    });
  },

  reactivateTag: async (id, loja_id) => {
    const res = await apiCall(`/api/tags/${id}/reactivate`, {
      method: 'POST'
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.error || 'Erro ao reativar tag');
    }

    const reactivated: CrmTag = await res.json();
    set(state => {
      const existing = state.tagsByLoja[loja_id] || [];
      const updated = existing.map(t => (t.id === id ? { ...t, ...reactivated, ativa: true } : t));
      return {
        tagsByLoja: { ...state.tagsByLoja, [loja_id]: updated }
      };
    });

    return reactivated;
  },

  resolveTag: (lojaId, tagKey) => {
    try {
      if (!tagKey) {
        return { label: '', color: '#6B7280', ativa: true, isKnown: false };
      }

      // Se porventura receber um objeto em vez de string, tenta extrair o label/nome
      const rawString = typeof tagKey === 'object' && tagKey !== null
        ? String((tagKey as any).label || (tagKey as any).nome || (tagKey as any).name || '')
        : String(tagKey);

      const cleanKey = rawString.trim();
      if (!cleanKey) {
        return { label: '', color: '#6B7280', ativa: true, isKnown: false };
      }

      const normalizedKey = cleanKey
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9_]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');

      const tagsDaLoja = lojaId ? get().tagsByLoja[lojaId] || [] : [];

      // ID is authoritative; legacy slug/display-name aliases stay within this store.
      const match = tagsDaLoja.find(t => t.id === cleanKey) || tagsDaLoja.find(
        t =>
          t.nome.toLowerCase() === cleanKey.toLowerCase() ||
          t.nome.toLowerCase() === normalizedKey ||
          (t.nome_exibicao && t.nome_exibicao.toLowerCase() === cleanKey.toLowerCase())
      );

      if (match) {
        return {
          id: match.id,
          label: match.nome_exibicao || match.nome,
          color: match.cor || '#3B82F6',
          ativa: match.ativa !== false,
          isKnown: true
        };
      }

      // Unknown tags stay visible/neutral; never borrow another store's catalog.
      return {
        label: cleanKey,
        color: '#6B7280', // Fallback neutro especificado
        ativa: true,
        isKnown: false
      };
    } catch {
      return {
        label: typeof tagKey === 'string' ? tagKey : '',
        color: '#6B7280',
        ativa: true,
        isKnown: false
      };
    }
  }
}));
